import { ref, computed, watch } from 'vue';
import { quranService } from '@/services/quranService';
import { RANGE_LIMITS } from '@/constants';

export function useQuranTest() {
  // State
  const chapters = ref([]);
  const verses = ref([]);
  const activeVerseIndex = ref(0);
  const isRepeat = ref(false);
  const isLoading = ref(true);
  const error = ref(null);
  
  // Config
  const mode = ref('words'); // 'words' | 'verses'
  const rangeType = ref('page'); // 'juz' | 'page' | 'key'
  const rangeValue = ref({ start: 3, end: 4 });
  const verseRangeValue = ref({
    startChapter: 1,
    startVerse: 1,
    endChapter: 1,
    endVerse: 7,
  });
  
  // Watch for changes and persist to localStorage
  watch(mode, (newMode) => {
    try {
      localStorage.setItem('quran_mode', newMode);
    } catch (e) {
      console.warn('Could not save mode to localStorage:', e);
    }
  });
  
  watch(rangeType, (newRangeType) => {
    try {
      localStorage.setItem('quran_rangeType', newRangeType);
    } catch (e) {
      console.warn('Could not save rangeType to localStorage:', e);
    }
  });
  
  watch(rangeValue, (newRangeValue) => {
    try {
      localStorage.setItem('quran_rangeValue', JSON.stringify(newRangeValue));
    } catch (e) {
      console.warn('Could not save rangeValue to localStorage:', e);
    }
  }, { deep: true });
  
  watch(verseRangeValue, (newVerseRangeValue) => {
    try {
      localStorage.setItem('quran_verseRangeValue', JSON.stringify(newVerseRangeValue));
    } catch (e) {
      console.warn('Could not save verseRangeValue to localStorage:', e);
    }
  }, { deep: true });
  
  watch(activeVerseIndex, (newIndex) => {
    try {
      localStorage.setItem('quran_activeVerseIndex', String(newIndex));
    } catch (e) {
      console.warn('Could not save activeVerseIndex to localStorage:', e);
    }
  });
  
  // Computed
  const currentVerse = computed(() => verses.value[activeVerseIndex.value]);
  const slicedVerses = computed(() => verses.value.slice(activeVerseIndex.value));
  const rangeLimit = computed(() => rangeType.value === 'page' ? RANGE_LIMITS.PAGE : RANGE_LIMITS.JUZ);
  
  const currentSurahName = computed(() => {
    if (isLoading.value || !chapters.value.length || !currentVerse.value) {
      return null;
    }
    return chapters.value.find(s => s.id === currentVerse.value.surah_id);
  });
  
  // Actions
  const fetchChapters = async () => {
    try {
      chapters.value = await quranService.getChapters();
    } catch (e) {
      error.value = e;
      throw e;
    }
  };
  
  const fetchVerses = async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const rangeValueStr = quranService.buildRangeValue(
        rangeType.value,
        rangeType.value === 'key' ? verseRangeValue.value : rangeValue.value
      );
      
      verses.value = await quranService.getVerses({
        rangeType: rangeType.value,
        rangeValue: rangeValueStr,
      });
      
      activeVerseIndex.value = 0;
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };
  
  const nextVerse = () => {
    if (!isRepeat.value) {
      activeVerseIndex.value = (activeVerseIndex.value + 1) % verses.value.length;
    }
  };
  
  const randomVerse = () => {
    activeVerseIndex.value = Math.floor(Math.random() * verses.value.length);
  };
  
  let savedActiveVerseIndex = null;
  
  const initConfig = () => {
    try {
      // Load mode
      const savedMode = localStorage.getItem('quran_mode');
      if (savedMode === 'words' || savedMode === 'verses') {
        mode.value = savedMode;
      }
      
      // Load rangeType
      const savedRangeType = localStorage.getItem('quran_rangeType');
      if (savedRangeType === 'juz' || savedRangeType === 'page' || savedRangeType === 'key') {
        rangeType.value = savedRangeType;
      }
      
      // Load rangeValue
      const savedRangeValue = localStorage.getItem('quran_rangeValue');
      if (savedRangeValue) {
        const parsed = JSON.parse(savedRangeValue);
        if (parsed && typeof parsed.start === 'number' && typeof parsed.end === 'number') {
          rangeValue.value = parsed;
        }
      }
      
      // Load verseRangeValue
      const savedVerseRangeValue = localStorage.getItem('quran_verseRangeValue');
      if (savedVerseRangeValue) {
        const parsed = JSON.parse(savedVerseRangeValue);
        if (parsed && 
            typeof parsed.startChapter === 'number' && 
            typeof parsed.startVerse === 'number' &&
            typeof parsed.endChapter === 'number' && 
            typeof parsed.endVerse === 'number') {
          verseRangeValue.value = parsed;
        }
      }
      
      // Load activeVerseIndex (stored for later restoration)
      const savedIndex = localStorage.getItem('quran_activeVerseIndex');
      if (savedIndex) {
        const parsed = parseInt(savedIndex, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          savedActiveVerseIndex = parsed;
        }
      }
    } catch (e) {
      console.warn('Could not load config from localStorage:', e);
    }
  };
  
  const restoreSavedVerseIndex = () => {
    if (savedActiveVerseIndex !== null && savedActiveVerseIndex < verses.value.length) {
      activeVerseIndex.value = savedActiveVerseIndex;
      savedActiveVerseIndex = null; // Clear it after restoring once
    }
  };
  
  return {
    // State
    chapters,
    verses,
    activeVerseIndex,
    isRepeat,
    isLoading,
    error,
    
    // Config
    mode,
    rangeType,
    rangeValue,
    verseRangeValue,
    
    // Computed
    currentVerse,
    slicedVerses,
    rangeLimit,
    currentSurahName,
    
    // Actions
    fetchChapters,
    fetchVerses,
    nextVerse,
    randomVerse,
    initConfig,
    restoreSavedVerseIndex,
  };
}
